import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { captureException } from '@sentry/react';
import {
	InMemoryWebStorage,
	User,
	UserManager,
	UserManagerSettings,
	WebStorageStateStore,
} from 'oidc-client-ts';

import { constants } from '@bedc/constants';

import { clientConstants } from '@bedc/client/constants';
import { storage } from '@bedc/client/utils';

import { clearLocalStoragePreferences } from './userPreferencesUtils';

/**
 * The utilities here are how we can use auth methods outside of React land
 * e.g. allowing our Axios service to get the latest access token.
 */

let authSettings = {
	region: '',
	userPoolId: '',
	clientId: '',
	identityPoolId: '',
	customDomain: '',
};

const getAuthSettings = () => authSettings;

/**
 * Sets the auth settings.
 *
 * @param region - AWS region
 * @param userPoolId - Cognito User Pool ID
 * @param clientId - Cognito User Pool Client ID
 * @param identityPoolId - Cognito Identity Pool ID
 */
export const setAuthSettings = (
	region: string,
	userPoolId: string,
	clientId: string,
	identityPoolId: string,
	customDomain: string
) =>
	(authSettings = {
		region,
		userPoolId,
		clientId,
		identityPoolId,
		customDomain,
	});

/**
 * Returns the logout Cognito URL to fully log a user out by clearing Cognito cookies.
 *
 * @returns The logout URL as a string.
 */
export const getLogOutUrl = () => {
	const { customDomain, clientId } = getAuthSettings();
	return `https://${customDomain}/logout?client_id=${clientId}&logout_uri=${window.location.origin}/login`;
};

/**
 * The URL of a session endpoint on the app's own origin.
 *
 * CloudFront routes these to the API, and the local dev server proxies them, so the cookies they
 * set are first-party. See `constants.api.routes.auth`.
 *
 * @param route - The endpoint.
 * @returns The absolute URL.
 */
const getSessionEndpoint = (route: 'token' | 'refresh' | 'logout' | 'handover') =>
	`${window.location.origin}${constants.api.routes.auth.prefix}${constants.api.routes.auth[route]}`;

/**
 * Where the signed-in user is kept: in memory, for this page only.
 *
 * Page scripts can read anything in `localStorage`, so a script injected into the page could copy
 * a session out of it. The refresh token now lives in an HttpOnly cookie instead, and a page that
 * loads restores its session by calling the refresh endpoint. One instance is shared so every
 * `UserManager` in the page sees the same user.
 */
const userStore = new WebStorageStateStore({ store: new InMemoryWebStorage() });

/**
 * OIDC auth configuration.
 *
 * @returns OidcClientSettings
 */
export const getOidcAuthConfig = (): UserManagerSettings => {
	const { region, userPoolId, clientId } = getAuthSettings();

	return {
		authority: `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
		client_id: clientId,
		redirect_uri: `${window.location.origin}${clientConstants.auth.LoginPath}`,
		response_type: 'code',
		scope: 'aws.cognito.signin.user.admin email openid phone profile',
		post_logout_redirect_uri: `${window.location.origin}${clientConstants.auth.LoginPath}`,
		userStore,
		// The sign-in callback posts its code here instead of to Cognito, which keeps the refresh
		// token server-side. The endpoint answers in the token endpoint's own format, so the rest of
		// the callback is unchanged. The PKCE state store stays in `localStorage`: it must survive
		// the redirect, and it never holds a token.
		metadataSeed: { token_endpoint: getSessionEndpoint('token') },
		// Disable automatic token renewal to prevent race conditions with axios-auth-refresh
		// Token refresh is handled reactively by axios interceptor on 401 responses
		automaticSilentRenew: false,
		monitorSession: false,
	};
};

/**
 * The refresh token of a session an older build stored in `localStorage`, held in memory until
 * a refresh gets a clear answer. See `readLegacyStoredSession`.
 */
let legacyRefreshToken: string | null = null;

/**
 * Reads the refresh token of a session an older build stored in `localStorage`.
 *
 * Before the session moved into cookies, the whole user was kept in `localStorage`, refresh
 * token included. When the first refresh finds no refresh cookie, it hands this token to the
 * server, which moves it into the cookie, so the switch signs nobody out. Temporary: remove
 * with the server's handover endpoint once those tokens have expired (LON-370).
 *
 * The stored session is deleted once a refresh gets a clear answer: the browser has a working
 * cookie session, or the server refuses the stored one. Until then it stays, so a refresh that
 * fails for another reason, such as a deploy that hasn't routed the session endpoints yet, can
 * try again, including after the page navigates to the login page.
 */
export const readLegacyStoredSession = () => {
	// oidc-client-ts kept the user under `oidc.user:<authority>:<client id>`.
	const { authority, client_id } = getOidcAuthConfig();

	legacyRefreshToken = readRefreshToken(storage.getItem(`oidc.user:${authority}:${client_id}`));

	if (!legacyRefreshToken) {
		discardLegacyStoredSession();
	}
};

/** Deletes the stored session from memory and from `localStorage`. */
const discardLegacyStoredSession = () => {
	legacyRefreshToken = null;
	storage.removeByPrefix('oidc.user:');
};

/**
 * Reads the refresh token from a user an older build stored.
 *
 * @param stored - The stored user, as JSON.
 * @returns The refresh token, if there is one.
 */
const readRefreshToken = (stored: string | null): string | null => {
	try {
		const user: unknown = JSON.parse(stored ?? 'null');

		return typeof user === 'object' &&
			user !== null &&
			'refresh_token' in user &&
			typeof user.refresh_token === 'string' &&
			user.refresh_token
			? user.refresh_token
			: null;
	} catch {
		return null;
	}
};

/**
 * Hands the stored refresh token to the server to start a cookie session from it.
 *
 * @param refreshToken - The refresh token an older build stored.
 * @returns The server's response, in the refresh endpoint's format.
 */
const handOverLegacySession = (refreshToken: string) =>
	fetch(getSessionEndpoint('handover'), {
		method: 'POST',
		credentials: 'same-origin',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			grant_type: 'refresh_token',
			refresh_token: refreshToken,
		}).toString(),
	});

/**
 * OIDC UserManager instance.
 *
 * The React app sets this via `setOidcUserManager()` when it loads to make sure that both
 * React and the utils are using the same instance. See `OidcAuthProvider.tsx`.
 *
 * Init with a default UserManager instance to avoid type errors.
 */
let oidcUserManager = new UserManager(getOidcAuthConfig());

export const setOidcUserManager = (userManager: UserManager) => {
	oidcUserManager = userManager;
};

/**
 * OIDC UserManager instance for handling the OIDC user.
 *
 * @example
 * ```ts
 * import { getOidcUserManager } from '@bedc/client/auth';
 * getOidcUserManager().getUser();
 */
export const getOidcUserManager = () => oidcUserManager;

/**
 * Flag to indicate if logout is currently in progress.
 * This is a synchronous flag (not React state) to prevent race conditions
 * where auto-redirect might trigger during logout.
 */
let isLoggingOut = false;

export const getIsLoggingOut = () => isLoggingOut;

/**
 * Shared promise for ongoing refresh sessions to prevent multiple concurrent calls.
 */
let refreshPromise: Promise<boolean> | null = null;

/**
 * The logout request in flight. A burst of failed requests can each call `logout`, and they
 * should revoke the session once.
 */
let logoutRequest: Promise<void> | null = null;

/**
 * Timestamp (ms) of the last successful silent refresh.
 */
let lastSuccessfulRefreshAt: number | null = null;

/**
 * The latest reason a session refresh could not complete.
 */
let lastRefreshFailureKind: AwsCredentialFailureKind = 'auth';

export type AwsCredentialFailureKind = 'auth' | 'network';

type CognitoCredentials = Awaited<ReturnType<ReturnType<typeof fromCognitoIdentityPool>>>;

export type AwsCredentialResult =
	| {
			status: 'success';
			credentials: CognitoCredentials;
	  }
	| {
			status: 'auth-error';
			reason:
				| 'session-refresh-failed'
				| 'session-read-failed'
				| 'missing-session'
				| 'expired-session'
				| 'missing-id-token'
				| 'identity-authorization-failed';
			error?: unknown;
	  }
	| {
			status: 'network-error';
			reason:
				| 'session-refresh-unavailable'
				| 'session-read-unavailable'
				| 'identity-pool-unavailable';
			error?: unknown;
	  };

/**
 * Determine whether an authentication operation failed because the network is unavailable.
 *
 * @param error The operation error.
 * @returns Whether the error represents a transient network failure.
 */
const isNetworkError = (error: unknown): boolean => {
	if (!navigator.onLine) {
		return true;
	}

	const errorName =
		error instanceof Error
			? error.name
			: typeof error === 'object' &&
				  error !== null &&
				  'name' in error &&
				  typeof error.name === 'string'
				? error.name
				: undefined;
	const httpStatusCode =
		typeof error === 'object' &&
		error !== null &&
		'$metadata' in error &&
		typeof error.$metadata === 'object' &&
		error.$metadata !== null &&
		'httpStatusCode' in error.$metadata &&
		typeof error.$metadata.httpStatusCode === 'number'
			? error.$metadata.httpStatusCode
			: undefined;

	return (
		(!!errorName &&
			[
				'AbortError',
				'ExternalServiceException',
				'InternalErrorException',
				'NetworkingError',
				'RequestTimeout',
				'RequestTimeoutException',
				'ServiceUnavailableException',
				'ThrottlingException',
				'TimeoutError',
				'TooManyRequestsException',
			].includes(errorName)) ||
		httpStatusCode === 408 ||
		httpStatusCode === 429 ||
		(httpStatusCode !== undefined && httpStatusCode >= 500) ||
		(error instanceof Error &&
			/network|fetch|timeout|timed out|connection|load failed/i.test(error.message))
	);
};

/**
 * Refreshes the authentication session.
 *
 * The refresh endpoint renews the session from its HttpOnly cookie, so this also restores the
 * session of a page that has just loaded and holds no user yet.
 *
 * Uses singleton pattern to prevent concurrent refresh operations.
 */
export const refreshSession = async (): Promise<boolean> => {
	// Return existing refresh if one is already in progress
	if (refreshPromise) {
		return refreshPromise;
	}

	// Create new refresh operation
	refreshPromise = refreshFromSessionCookie();

	try {
		const result = await refreshPromise;
		if (result) {
			return true;
		}
		throw new Error('Refresh failed');
	} catch {
		// TODO: Logging out here is too aggressive.
		// await logout();
		return false;
	} finally {
		refreshPromise = null;
	}
};

/** The tokens the session endpoints return. The refresh token never leaves its cookie. */
type SessionTokens = {
	access_token: string;
	id_token: string;
	token_type: string;
	expires_in: number;
};

/** Claims oidc-client-ts leaves out of a user's profile. */
const protocolClaims = ['nbf', 'jti', 'auth_time', 'nonce', 'acr', 'amr', 'azp', 'at_hash'];

/**
 * Builds the signed-in user from refreshed tokens, the way oidc-client-ts builds one at sign-in.
 *
 * The ID token is decoded only to read the profile. The API and the identity pool verify it.
 *
 * @param tokens - The refreshed tokens.
 * @param scope - The scopes the session was granted.
 * @returns The user.
 */
const createUser = (tokens: SessionTokens, scope: string) => {
	const payload = tokens.id_token.split('.')[1] ?? '';
	const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
	const bytes = Uint8Array.from(json, (char) => char.charCodeAt(0));
	const claims = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;

	for (const claim of protocolClaims) {
		delete claims[claim];
	}

	return new User({
		access_token: tokens.access_token,
		id_token: tokens.id_token,
		token_type: tokens.token_type,
		scope,
		profile: claims as User['profile'],
		expires_at: Math.floor(Date.now() / 1000) + tokens.expires_in,
	});
};

async function refreshFromSessionCookie(): Promise<boolean> {
	const userManager = getOidcUserManager();
	let existingUser: User | null = null;

	try {
		// Check if token was already refreshed by another concurrent call
		existingUser = await userManager.getUser();
		if (existingUser && !existingUser.expired) {
			return true;
		}

		const refreshFromCookie = () =>
			fetch(getSessionEndpoint('refresh'), { method: 'POST', credentials: 'same-origin' });
		let response = await refreshFromCookie();

		// No refresh cookie yet, but an older build left a session behind. Hand it over.
		if (response.status === 401 && legacyRefreshToken) {
			response = await handOverLegacySession(legacyRefreshToken);

			// The handover refuses a browser that already has a refresh cookie. Another tab
			// handed the session over first, so the cookie works now.
			if (response.status === 400) {
				response = await refreshFromCookie();
			}
		}

		// No session to restore: there is no refresh cookie, or Cognito no longer accepts it. The
		// handover answers 400 when it refuses the request.
		if (response.status === 401 || response.status === 400) {
			discardLegacyStoredSession();
			lastRefreshFailureKind = 'auth';
			return false;
		}

		if (!response.ok) {
			throw Object.assign(new Error(`Session refresh failed (${response.status})`), {
				$metadata: { httpStatusCode: response.status },
			});
		}

		// The endpoint also renewed the edge cookie. Hand the user to the auth context, which
		// listens for loaded users.
		const user = createUser(
			(await response.json()) as SessionTokens,
			userManager.settings.scope ?? ''
		);
		// The browser has a working cookie session, so the stored one is no longer needed. Keeping
		// it would let a later refresh restore a session the user has since signed out of.
		discardLegacyStoredSession();
		await userManager.storeUser(user);
		await userManager.events.load(user);

		lastSuccessfulRefreshAt = Date.now();
		lastRefreshFailureKind = 'auth';

		return true;
	} catch (refreshError) {
		lastRefreshFailureKind = isNetworkError(refreshError) ? 'network' : 'auth';

		try {
			captureException(refreshError, (scope) => {
				scope.setTransactionName('[userAuthUtils] refreshFromSessionCookie() error');
				scope.setContext('refreshDiagnostics', {
					online: navigator.onLine,
					visibilityState: document.visibilityState,
					hadExistingUser: !!existingUser,
					existingUserExpired: existingUser?.expired ?? null,
					existingUserExpiresIn: existingUser?.expires_in ?? null,
					msSinceLastSuccess:
						lastSuccessfulRefreshAt !== null
							? Date.now() - lastSuccessfulRefreshAt
							: null,
					errorName:
						refreshError instanceof Error ? refreshError.name : typeof refreshError,
					errorMessage:
						refreshError instanceof Error ? refreshError.message : String(refreshError),
				});
				return scope;
			});
		} catch {
			// Ignore Sentry capture errors.
		}

		return false;
	}
}

/**
 * Fetches the latest AWS credentials.
 *
 * @param forceRefresh Whether to refresh the OIDC session before requesting credentials.
 * @returns A controlled credential result.
 */
export const getAwsCredentials = async (forceRefresh = false): Promise<AwsCredentialResult> => {
	if (forceRefresh) {
		const refreshed = await refreshSession();
		if (!refreshed) {
			return lastRefreshFailureKind === 'network'
				? {
						status: 'network-error',
						reason: 'session-refresh-unavailable',
					}
				: {
						status: 'auth-error',
						reason: 'session-refresh-failed',
					};
		}
	}

	let user: User | null;
	try {
		user = await getOidcUserManager().getUser();
	} catch (error) {
		return isNetworkError(error)
			? {
					status: 'network-error',
					reason: 'session-read-unavailable',
					error,
				}
			: {
					status: 'auth-error',
					reason: 'session-read-failed',
					error,
				};
	}

	if (!user) {
		return { status: 'auth-error', reason: 'missing-session' };
	}
	if (user.expired) {
		return { status: 'auth-error', reason: 'expired-session' };
	}
	if (!user.id_token) {
		return { status: 'auth-error', reason: 'missing-id-token' };
	}

	try {
		const credentials = await getCredentialsFromCognitoIdentityPool(user.id_token);
		return { status: 'success', credentials };
	} catch (error) {
		if (isNetworkError(error)) {
			return {
				status: 'network-error',
				reason: 'identity-pool-unavailable',
				error,
			};
		}

		return {
			status: 'auth-error',
			reason: 'identity-authorization-failed',
			error,
		};
	}
};

export const getCredentialsFromCognitoIdentityPool = async (idToken: string) => {
	const { region, identityPoolId, userPoolId } = getAuthSettings();

	// Get credentials from Cognito Identity Pool
	const credentialsProvider = fromCognitoIdentityPool({
		clientConfig: { region },
		identityPoolId,
		logins: {
			[`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken,
		},
	});

	return await credentialsProvider();
};

/**
 * Fetches the latest access token.
 * @returns The latest access token as a string.
 */
export const getAccessToken = async () => {
	const oidcAccessToken = (await getOidcUserManager().getUser())?.access_token;
	if (oidcAccessToken) {
		return oidcAccessToken;
	}
};

/**
 * Fetches an access token that has not expired, refreshing the session first
 * when the stored token has.
 *
 * `getAccessToken()` returns the stored token as it is. That suits axios, which
 * refreshes after a 401 and replays the request. A websocket handshake has no
 * such retry: the server answers an expired token with 403 and the client would
 * reconnect with the same token until it gave up.
 *
 * @returns The access token, or `undefined` when there is no signed-in user.
 * @throws When the stored token has expired and the session cannot be refreshed.
 */
export const getValidAccessToken = async () => {
	const user = await getOidcUserManager().getUser();

	if (!user?.access_token) {
		return undefined;
	}

	if (!user.expired) {
		return user.access_token;
	}

	if (!(await refreshSession())) {
		throw new Error('Unable to refresh the expired access token');
	}

	return getAccessToken();
};

/**
 * Fetches the latest id token.
 * @returns The latest access token as a string.
 */
export const getIdToken = async () => {
	const oidcIdToken = (await getOidcUserManager().getUser())?.id_token;
	if (oidcIdToken) {
		return oidcIdToken;
	}
};

/**
 * Remove any local storage references to auth.
 * By default not all keys are removed from local storage by
 * React OIDC context when signing out.
 * */
export const clearAuthFromStorage = () => {
	const clear = (storage: Storage) => {
		for (let i = storage.length - 1; i >= 0; i--) {
			const key = storage.key(i);
			if (
				key &&
				(key.startsWith('CognitoIdentityServiceProvider.') || key.startsWith('oidc.'))
			) {
				storage.removeItem(key);
			}
		}
	};

	clear(localStorage);
	clear(sessionStorage);
	legacyRefreshToken = null;
};

/**
 * Sign the user out.
 *
 * @param onLogout - Optional callback function to call after logout
 */
export const logout = async (onLogout?: () => void) => {
	// Set synchronous flag to prevent auto-redirect during logout
	isLoggingOut = true;

	// Revoke the refresh token and clear the session cookies. Page scripts cannot clear them
	// themselves, because they are HttpOnly.
	logoutRequest ??= fetch(getSessionEndpoint('logout'), {
		method: 'POST',
		credentials: 'same-origin',
	}).then(
		() => undefined,
		// Ignore: the redirect below still ends the Cognito session, and the cookies expire on
		// their own.
		() => undefined
	);
	await logoutRequest;

	// Sign out via OIDC
	try {
		await getOidcUserManager().signoutSilent();
	} catch {
		// Ignore errors from OIDC sign out.
		// We hard remove auth from storage so this is not critical
		// + we are redirecting to the logout URL which will clear OIDC cookies.
	}

	// Remove any local storage references to auth.
	clearAuthFromStorage();

	// Clear preferences from local storage.
	clearLocalStoragePreferences();

	// Call the onLogout callback if provided.
	onLogout?.();

	// Redirect to the logout URL.
	// This will clear OIDC cookies on the managed login page and redirect to the login page.
	window.location.href = getLogOutUrl();
};

/**
 * Creates a session key for storing the redirect URL in session storage.
 *
 * @param sessionId - The session ID to create the key for.
 * @returns The session key as a string.
 */
export const createAuthSessionKey = (sessionId: string) => `auth_redirect_${sessionId}`;

/**
 * Returns a post-login redirect target only when it is a path on this site.
 *
 * The target arrives in the login page's query string, so anyone can link to `/login` with an
 * arbitrary one. An absolute or protocol-relative URL (`https://…`, `//…`, `/\…`) would send a
 * freshly signed-in user to another site.
 *
 * The target is resolved the way the browser will resolve it, because the URL parser drops tabs and
 * newlines and reads a backslash as a slash: `/<tab>/evil.com` passes a prefix check but lands on
 * `https://evil.com/`. The parsed path is returned so the browser navigates to what was checked.
 *
 * @param redirectTo - The requested redirect target.
 * @returns The path, or `null` when it is missing or leaves the site.
 */
export const getSafeRedirectPath = (redirectTo: string | null | undefined): string | null => {
	if (!redirectTo?.startsWith('/')) {
		return null;
	}

	try {
		const url = new URL(redirectTo, window.location.origin);

		return url.origin === window.location.origin
			? `${url.pathname}${url.search}${url.hash}`
			: null;
	} catch {
		return null;
	}
};
